import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Location } from '../types/location';

interface LocationState {
  location: Location | null;
  currentUserLocation: Location | null;
  isLocationVerified: boolean | null; // true = within radius, false = outside radius, null = not checked
  verificationDistance: number | null; // distance in meters
  allowedRadius: number; // allowed radius in meters
  loading: boolean;
  error: string | null;
}

// Helper functions for localStorage
const saveToLocalStorage = (key: string, data: any) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

const getFromLocalStorage = (key: string) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return null;
  }
};

const loadInitialState = (): LocationState => {
  const savedCurrentUserLocation = getFromLocalStorage('currentUserLocation');
  const savedIsLocationVerified = getFromLocalStorage('isLocationVerified');
  const savedVerificationDistance = getFromLocalStorage('verificationDistance');
  const savedAllowedRadius = getFromLocalStorage('allowedRadius');
  
  return {
    location: null,
    currentUserLocation: savedCurrentUserLocation,
    isLocationVerified: savedIsLocationVerified,
    verificationDistance: savedVerificationDistance,
    allowedRadius: savedAllowedRadius || 150, // default 150 meters
    loading: false,
    error: null,
  };
};

const initialState: LocationState = loadInitialState();

const userLocationSlice = createSlice({
  name: 'userLocation',
  initialState,
  reducers: {
    fetchlocationStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchlocationSuccess: (state, action: PayloadAction<Location[]>) => {
      state.loading = false;
      state.location = action.payload[0] || null;
    },
    fetchlocationFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    addlocationStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    addlocationSuccess: (state, action: PayloadAction<Location>) => {
      state.loading = false;
      state.location = action.payload;
    },
    addlocationFailure: (state, action: PayloadAction<Location>) => {
      state.loading = false;
      state.location = action.payload;
    },
    // New action to store current user location
    setCurrentUserLocation: (state, action: PayloadAction<Location>) => {
      state.currentUserLocation = action.payload;
      state.error = null;
      // Save to localStorage
      saveToLocalStorage('currentUserLocation', action.payload);
    },
    // Clear current user location
    clearCurrentUserLocation: (state) => {
      state.currentUserLocation = null;
      // Remove from localStorage
      localStorage.removeItem('currentUserLocation');
    },
    // Set location verification status
    setLocationVerificationStatus: (state, action: PayloadAction<{
      isVerified: boolean;
      distance: number;
      allowedRadius?: number;
    }>) => {
      state.isLocationVerified = action.payload.isVerified;
      state.verificationDistance = action.payload.distance;
      if (action.payload.allowedRadius !== undefined) {
        state.allowedRadius = action.payload.allowedRadius;
      }
      state.error = null;
      
      // Save to localStorage
      saveToLocalStorage('isLocationVerified', action.payload.isVerified);
      saveToLocalStorage('verificationDistance', action.payload.distance);
      if (action.payload.allowedRadius !== undefined) {
        saveToLocalStorage('allowedRadius', action.payload.allowedRadius);
      }
    },
    // Clear location verification status
    clearLocationVerificationStatus: (state) => {
      state.isLocationVerified = null;
      state.verificationDistance = null;
      // Remove from localStorage
      localStorage.removeItem('isLocationVerified');
      localStorage.removeItem('verificationDistance');
    },
    // Set allowed radius
    setAllowedRadius: (state, action: PayloadAction<number>) => {
      state.allowedRadius = action.payload;
      // Save to localStorage
      saveToLocalStorage('allowedRadius', action.payload);
    },
    // Set location error
    setLocationError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    // Clear location error
    clearLocationError: (state) => {
      state.error = null;
    },
    // Reset all location state
    resetLocationState: (state) => {
      state.currentUserLocation = null;
      state.isLocationVerified = null;
      state.verificationDistance = null;
      state.error = null;
      state.loading = false;
      
      // Clear all location data from localStorage
      localStorage.removeItem('currentUserLocation');
      localStorage.removeItem('isLocationVerified');
      localStorage.removeItem('verificationDistance');
      localStorage.removeItem('allowedRadius');
    },
    // Restore from localStorage
    restoreFromStorage: (state) => {
      const savedCurrentUserLocation = getFromLocalStorage('currentUserLocation');
      const savedIsLocationVerified = getFromLocalStorage('isLocationVerified');
      const savedVerificationDistance = getFromLocalStorage('verificationDistance');
      const savedAllowedRadius = getFromLocalStorage('allowedRadius');
      
      if (savedCurrentUserLocation) state.currentUserLocation = savedCurrentUserLocation;
      if (savedIsLocationVerified !== null) state.isLocationVerified = savedIsLocationVerified;
      if (savedVerificationDistance !== null) state.verificationDistance = savedVerificationDistance;
      if (savedAllowedRadius !== null) state.allowedRadius = savedAllowedRadius;
    }
  },
  extraReducers: (builder) => {
    // No extra reducers needed since we are not using createAsyncThunk
  },
});

export const {
  fetchlocationStart,
  fetchlocationSuccess,
  fetchlocationFailure,
  addlocationStart,
  addlocationSuccess,
  addlocationFailure,
  setCurrentUserLocation,
  clearCurrentUserLocation,
  setLocationVerificationStatus,
  clearLocationVerificationStatus,
  setAllowedRadius,
  setLocationError,
  clearLocationError,
  resetLocationState,
  restoreFromStorage,
} = userLocationSlice.actions;

export default userLocationSlice.reducer;